// Simple API client for Pinnacle Payment Processor Web

const BASE_URL = import.meta.env.VITE_BASE_URL || "";

function getToken() {
  // Try to get token from localStorage user object
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return user?.token || null;
  } catch {
    return null;
  }
}

function getHeaders(token: string | null, extra: Record<string, string> = {}) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...extra,
  };
  // Use provided token or fallback to localStorage
  const authToken = token || getToken();
  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
  return headers;
}

export async function apiRequest({
  endpoint,
  method = "GET",
  data = null,
  token = null,
  params = null,
  headers = {},
  withAuth = false, // If true, always add Authorization header
  signal = null, // AbortController signal for request cancellation
}: any) {
  let url = BASE_URL + endpoint;
  if (params && typeof params === "object") {
    const search = new URLSearchParams(params).toString();
    url += `?${search}`;
  }
  const options: any = {
    method,
    headers: getHeaders(withAuth ? token || getToken() : token, headers),
  };
  if (data) {
    if (data instanceof FormData) {
      // For FormData, don't set Content-Type header (browser will set it with boundary)
      // Remove Content-Type from headers to let browser handle multipart/form-data
      delete options.headers["Content-Type"];
      options.body = data;
    } else {
      options.body = JSON.stringify(data);
    }
  }
  if (signal) {
    options.signal = signal;
  }
  const response = await fetch(url, options);
  let responseData;
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    responseData = await response.json();
  } else {
    responseData = await response.text();
  }
  if (!response.ok) {
    // The status travels with the message: "refused" and "broke" are different
    // answers, and a caller can only tell them apart if it is told which one it
    // got. A 403 is the API saying this session may not, which is an answer
    // worth acting on rather than retrying.
    const error: any = new Error(
      responseData?.message || responseData || "API request failed"
    );
    error.status = response.status;
    throw error;
  }
  return responseData;
}

// Fetches raw bytes (a previewed PDF or photo) with the session attached. The
// caller receives a Blob and decides how to display it.
export async function apiBlob({
  endpoint,
  params = null,
  token = null,
  withAuth = false,
  signal = null,
}: any) {
  let url = BASE_URL + endpoint;
  if (params && typeof params === "object") {
    url += `?${new URLSearchParams(params).toString()}`;
  }
  const response = await fetch(url, {
    headers: getHeaders(withAuth ? token || getToken() : token),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) {
    let message = "API request failed";
    try {
      const data = await response.json();
      message = data?.message || message;
    } catch {
      // The body was not JSON; the status is still the answer.
    }
    const error: any = new Error(message);
    error.status = response.status;
    throw error;
  }
  return response.blob();
}

// Convenience methods
export const apiGet = (args: any) => apiRequest({ ...args, method: "GET" });
export const apiPost = (args: any) => apiRequest({ ...args, method: "POST" });
export const apiPut = (args: any) => apiRequest({ ...args, method: "PUT" });
export const apiDelete = (args: any) => apiRequest({ ...args, method: "DELETE" });
